import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, X, ArrowRightCircle } from "lucide-react";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  }
};

// Reading Progress Bar
const ReadingProgressBar = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPosition = window.scrollY;
      const currentProgress = (scrollPosition / totalHeight) * 100;
      setProgress(currentProgress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="fixed top-0 left-0 w-full h-[2px] bg-transparent z-[60]">
      <div 
        className="h-full bg-truvio-primary transition-all duration-100 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// Hamburger Menu Icon
const HamburgerIcon = ({ isOpen }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    {isOpen ? (
      <>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </>
    ) : (
      <>
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </>
    )}
  </svg>
);

// Navigation Component
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="fixed top-0 w-full z-50 backdrop-blur-md border-b border-truvio-border bg-truvio-bg/80">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 flex items-center justify-between py-4">
        <Link to="/" className="font-playfair text-xl md:text-2xl font-black text-truvio-text flex-shrink-0">
          Truv<span className="italic text-truvio-primary">io</span>
        </Link>
        
        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          <span className="pill-tag">In development</span>
          <Link 
            to="/manifesto" 
            className="text-truvio-accent text-sm flex items-center gap-1 whitespace-nowrap"
          >
            Read our manifesto <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Mobile hamburger button */}
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-truvio-text p-2"
          aria-label="Toggle menu"
        >
          <HamburgerIcon isOpen={mobileMenuOpen} />
        </button>
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-truvio-border bg-truvio-surface">
          <div className="max-w-[1200px] mx-auto px-6 py-4 flex flex-col gap-4">
            <span className="pill-tag w-fit">In development</span>
            <Link 
              to="/manifesto" 
              onClick={() => setMobileMenuOpen(false)}
              className="text-truvio-accent text-sm flex items-center gap-1"
            >
              Read our manifesto <ArrowRight className="w-3 h-3" />
            </Link>
            <Link 
              to="/" 
              onClick={() => setMobileMenuOpen(false)}
              className="text-truvio-muted hover:text-truvio-text text-sm"
            >
              Back to home
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

// Pull Quote Component
const PullQuote = ({ children }) => (
  <motion.blockquote 
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeUp}
    className="my-12 pl-6 border-l-[3px] border-truvio-primary bg-truvio-surface/50 py-6 pr-6 -mx-4 md:mx-0"
  >
    <p className="font-playfair text-xl md:text-2xl italic text-truvio-text leading-relaxed">
      {children}
    </p>
  </motion.blockquote>
);

// Callout Box Component
const CalloutBox = ({ children }) => (
  <motion.div 
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeUp}
    className="my-10 pl-6 border-l-[3px] border-truvio-primary bg-truvio-surface p-6 -mx-4 md:mx-0"
  >
    <p className="text-truvio-muted text-sm leading-relaxed">
      {children}
    </p>
  </motion.div>
);

// Chapter Component
const Chapter = ({ number, title, tag, children }) => (
  <motion.section 
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-100px" }}
    variants={fadeUp}
    id={`chapter-${number.toLowerCase()}`}
    className="mb-20 md:mb-28 scroll-mt-24"
  >
    <div className="flex items-center gap-4 mb-6">
      <span className="text-truvio-muted text-sm font-medium tracking-wider">{number}</span>
      {tag && (
        <span className="text-[10px] uppercase tracking-widest text-truvio-primary bg-truvio-primary/10 px-2 py-1 rounded-sm">
          {tag}
        </span>
      )}
    </div>
    <div className="h-px bg-truvio-border mb-8" />
    <h2 className="font-playfair font-black text-2xl md:text-3xl lg:text-4xl text-truvio-text mb-8 leading-tight">
      {title}
    </h2>
    <div className="prose-manifesto">
      {children}
    </div>
  </motion.section>
);

// Belief Item Component
const BeliefItem = ({ children }) => (
  <motion.div 
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeUp}
    className="flex gap-4 py-5 border-b border-truvio-border"
  >
    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-truvio-primary/20 flex items-center justify-center mt-0.5">
      <Check className="w-3.5 h-3.5 text-truvio-primary" />
    </div>
    <p className="text-truvio-text/90 leading-relaxed">{children}</p>
  </motion.div>
);

// Built Item Component
const BuiltItem = ({ icon, children }) => (
  <motion.div 
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeUp}
    className="bg-truvio-surface border border-truvio-border p-6 md:p-8 mb-4"
  >
    <div className="flex gap-4 md:gap-6">
      <div className="flex-shrink-0">{icon}</div>
      <p className="text-truvio-text/90 leading-relaxed">{children}</p>
    </div>
  </motion.div>
);

// Not Item Component
const NotItem = ({ type, children }) => (
  <motion.div 
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeUp}
    className="flex gap-4 py-4"
  >
    {type === 'negative' ? (
      <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
    ) : (
      <ArrowRightCircle className="w-5 h-5 text-truvio-primary flex-shrink-0 mt-0.5" />
    )}
    <p className="text-truvio-text/90 leading-relaxed">{children}</p>
  </motion.div>
);

// Table of Contents Component
const TableOfContents = () => {
  const chapters = [
    { num: "I", title: "The review economy was supposed to work" },
    { num: "II", title: "The first failure: reviews became a product" },
    { num: "III", title: "The second failure: reviews became a weapon" },
    { num: "IV", title: "The result: a system nobody can trust" },
    { num: "V", title: "What we believe" },
    { num: "VI", title: "What we built" },
    { num: "VII", title: "What we are not" },
    { num: "VIII", title: "A note on what we named ourselves" },
  ];

  const scrollToChapter = (num) => {
    const element = document.getElementById(`chapter-${num.toLowerCase()}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <motion.nav 
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      variants={fadeUp}
      className="mb-20 md:mb-28"
    >
      <h3 className="text-[10px] uppercase tracking-[0.2em] text-truvio-muted mb-6">Contents</h3>
      <div className="space-y-3">
        {chapters.map((ch) => (
          <button
            key={ch.num}
            onClick={() => scrollToChapter(ch.num)}
            className="flex text-left w-full group"
          >
            <span className="text-truvio-muted text-sm min-w-[2.5rem]">{ch.num}</span>
            <span className="text-truvio-text/80 group-hover:text-truvio-accent transition-colors">
              {ch.title}
            </span>
          </button>
        ))}
      </div>
    </motion.nav>
  );
};

// Footer Component
const Footer = () => (
  <footer className="py-10 md:py-16 border-t border-truvio-border">
    <div className="max-w-[1200px] mx-auto px-6 md:px-12">
      <div className="flex flex-col gap-8 md:flex-row md:justify-between md:items-center md:gap-8">
        <Link to="/" className="font-playfair text-xl md:text-2xl font-black text-truvio-text">
          Truv<span className="italic text-truvio-primary">io</span>
        </Link>
        
        <nav className="flex items-center gap-8 md:gap-10 text-xs md:text-sm">
          <Link to="/manifesto" className="text-truvio-accent transition-colors duration-300">
            Manifesto
          </Link>
          <Link to="/privacy" className="text-truvio-muted hover:text-truvio-text transition-colors duration-300">
            Privacy
          </Link>
          <a href="mailto:hello@truvio.me" className="text-truvio-muted hover:text-truvio-text transition-colors duration-300">
            Contact
          </a>
        </nav>

        <div className="text-left md:text-right">
          <p className="text-truvio-muted/70 text-[0.6875rem] md:text-xs tracking-wide">
            © 2026 Truvio · Registered in Cyprus · Data stored in the EU
          </p>
          <p className="text-truvio-muted/40 text-[0.625rem] mt-1.5 italic tracking-wide">
            truvio.me
          </p>
        </div>
      </div>
    </div>
  </footer>
);

// Main Manifesto Page
function ManifestoPage() {
  useEffect(() => {
    document.title = "The Truvio Manifesto — Why We Built This";
  }, []);

  return (
    <div className="bg-truvio-bg min-h-screen relative">
      {/* Ambient glows */}
      <div className="glow-green"></div>
      <div className="glow-lime"></div>
      
      {/* Grain overlay */}
      <div className="grain-overlay"></div>
      
      {/* Reading progress bar */}
      <ReadingProgressBar />
      
      {/* Content */}
      <div className="relative z-10">
        <Navigation />
        
        {/* Cover */}
        <header className="pt-32 md:pt-40 pb-16 md:pb-24">
          <div className="max-w-[760px] mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-[10px] uppercase tracking-[0.2em] text-truvio-muted mb-6 block">
                Manifesto
              </span>
              <h1 className="font-playfair font-black text-4xl md:text-5xl lg:text-6xl text-truvio-text leading-[1.1] mb-8">
                We believe trust is not a feature. It's a foundation.
              </h1>
              <p className="text-truvio-text/80 text-lg md:text-xl leading-relaxed mb-8">
                This is not a product description. It's not a pitch. It's what we actually think — about reviews, about accountability, about what the internet owes the people who use it.
              </p>
              <p className="text-truvio-muted text-sm italic">
                First published March 2026 · Living document
              </p>
            </motion.div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-[760px] mx-auto px-6 pb-20">
          
          <TableOfContents />

          {/* Chapter I */}
          <Chapter number="I" title="The review economy was supposed to work.">
            <p>
              The idea was simple and good: give ordinary people a{'\u00A0'}way to{'\u00A0'}share their experiences. Let markets self-correct through transparency. Create a{'\u00A0'}world where a{'\u00A0'}small business that treats customers well competes on{'\u00A0'}equal footing with a{'\u00A0'}large one that doesn't.
            </p>
            <p>
              For a{'\u00A0'}while, it{'\u00A0'}worked. Reviews genuinely changed behaviour. Companies improved because they had to. Customers made better decisions because they could.
            </p>
            <p>
              Then two things happened. And they happened slowly enough that nobody sounded the alarm until the damage was done.
            </p>
          </Chapter>

          {/* Chapter II */}
          <Chapter number="II" title="The first failure: reviews became a product." tag="First failure">
            <p>
              Review platforms need revenue. The obvious customer is{'\u00A0'}the business being reviewed{'\u00A0'}— they're the ones with money, and they're the ones who care most about the outcome.
            </p>
            <p>
              So platforms built subscription products. Analytics. Response tools. Premium features. All reasonable on{'\u00A0'}the surface. But underneath, a{'\u00A0'}conflict of{'\u00A0'}interest was taking shape that would eventually consume the entire value proposition.
            </p>
            <p>
              When a{'\u00A0'}business pays a{'\u00A0'}platform, the platform has an{'\u00A0'}incentive to{'\u00A0'}keep that business happy. Happy businesses don't cancel subscriptions. Unhappy businesses{'\u00A0'}— ones staring at{'\u00A0'}a{'\u00A0'}page full of{'\u00A0'}negative reviews they can't remove{'\u00A0'}— do.
            </p>
            <p>
              The result was predictable. Negative reviews started disappearing for paying customers. Ratings on{'\u00A0'}premium plans looked cleaner than ratings on{'\u00A0'}free ones. The algorithm quietly learned to{'\u00A0'}favour the people writing the cheques.
            </p>
            
            <CalloutBox>
              <strong>Documented:</strong> In{'\u00A0'}March 2019, The Times reported how UK estate agents were gaming Trustpilot{'\u00A0'}— one going from 5{'\u00A0'}reviews averaging 2.2{'\u00A0'}stars to{'\u00A0'}467{'\u00A0'}reviews, 90% of{'\u00A0'}them five stars, in{'\u00A0'}a{'\u00A0'}single month. In{'\u00A0'}December 2025, Grizzly Research published an{'\u00A0'}investigation describing what they called the 'Trustpilot Mafia': a{'\u00A0'}model where negative reviews disappeared once businesses paid a{'\u00A0'}monthly subscription. grizzlyreports.com/trustpilot
            </CalloutBox>

            <p>
              We are not making a{'\u00A0'}legal claim about any platform. We are observing a{'\u00A0'}structural problem: when a{'\u00A0'}platform's revenue depends on{'\u00A0'}the businesses it{'\u00A0'}reviews, the independence of{'\u00A0'}those reviews is{'\u00A0'}always at{'\u00A0'}risk. Not because of{'\u00A0'}bad intentions. Because of{'\u00A0'}incentives.
            </p>

            <PullQuote>
              A{'\u00A0'}rating that can be{'\u00A0'}bought is{'\u00A0'}not a{'\u00A0'}rating. It's advertising.
            </PullQuote>
          </Chapter>

          {/* Chapter III */}
          <Chapter number="III" title="The second failure: reviews became a weapon." tag="Second failure">
            <p>
              This one is{'\u00A0'}talked about less. Perhaps because it's uncomfortable{'\u00A0'}— it{'\u00A0'}implicates consumers, not corporations. But it{'\u00A0'}is{'\u00A0'}equally real, equally damaging, and in{'\u00A0'}some ways more corrosive.
            </p>
            <p>
              Negative reviews are powerful. A{'\u00A0'}single 1-star review on{'\u00A0'}a{'\u00A0'}high-traffic platform can move a{'\u00A0'}business's rating, trigger customer hesitation, and cost real money. Organised review campaigns can be{'\u00A0'}existential for a{'\u00A0'}small business.
            </p>
            <p>
              People noticed.
            </p>
            <p>
              Today there are organised communities, forums, and publicly accessible websites with step-by-step guides explaining how to{'\u00A0'}use a{'\u00A0'}review platform as{'\u00A0'}leverage. The playbook is{'\u00A0'}consistent: make a{'\u00A0'}purchase, find a{'\u00A0'}reason to{'\u00A0'}be{'\u00A0'}dissatisfied{'\u00A0'}— real or{'\u00A0'}manufactured{'\u00A0'}— file a{'\u00A0'}1-star review, make the threat of{'\u00A0'}more reviews explicit, and demand a{'\u00A0'}refund, replacement, or{'\u00A0'}settlement.
            </p>
            <p>
              Reviews on{'\u00A0'}major platforms are specifically named in{'\u00A0'}these guides as{'\u00A0'}among the most effective tools available. Not because they reflect genuine experience. Because businesses are afraid of{'\u00A0'}them.
            </p>

            <PullQuote>
              This is{'\u00A0'}first-party fraud. It{'\u00A0'}is{'\u00A0'}organised, widespread, and directly harms businesses that did nothing wrong.
            </PullQuote>

            <p>
              It{'\u00A0'}also does something subtler and more lasting: it{'\u00A0'}makes every legitimate negative review less credible. When a{'\u00A0'}business receives a{'\u00A0'}coordinated attack and a{'\u00A0'}genuine customer complaint, and they look identical{'\u00A0'}— the genuine one loses its meaning.
            </p>
            <p>
              The fraud doesn't just harm businesses. It{'\u00A0'}harms the honest reviewers whose voices it{'\u00A0'}drowns out.
            </p>
          </Chapter>

          {/* Chapter IV */}
          <Chapter number="IV" title="The result: a system nobody can trust.">
            <p>
              Businesses are caught between two threats: platforms that profit from selling them protection from criticism, and consumers who have learned to{'\u00A0'}use criticism as{'\u00A0'}a{'\u00A0'}weapon.
            </p>
            <p>
              Honest customers are reading ratings shaped by{'\u00A0'}money on{'\u00A0'}one side and extortion on{'\u00A0'}the other.
            </p>
            <p>
              Genuine reviewers{'\u00A0'}— people with real experiences and real things to{'\u00A0'}say{'\u00A0'}— are publishing into a{'\u00A0'}void where nobody can be{'\u00A0'}sure if{'\u00A0'}their words carry weight or{'\u00A0'}get buried.
            </p>
            <p>
              The review economy, which should be{'\u00A0'}one of{'\u00A0'}the most powerful forces for market accountability ever created, has been captured by{'\u00A0'}the worst incentives available to{'\u00A0'}it.
            </p>
            <p>
              We don't think this was inevitable. We think it{'\u00A0'}was the predictable result of{'\u00A0'}specific structural choices. And we think those choices can be{'\u00A0'}unmade.
            </p>
          </Chapter>

          {/* Chapter V */}
          <Chapter number="V" title="What we believe.">
            <div className="mt-8">
              <BeliefItem>
                A{'\u00A0'}review is{'\u00A0'}only valuable if{'\u00A0'}the person writing it{'\u00A0'}is{'\u00A0'}accountable for what they say.
              </BeliefItem>
              <BeliefItem>
                Accountability requires identity. Not an{'\u00A0'}email address. Not a{'\u00A0'}username. A{'\u00A0'}real, verified identity{'\u00A0'}— one that carries legal weight and social consequence.
              </BeliefItem>
              <BeliefItem>
                A{'\u00A0'}negative review is{'\u00A0'}one of{'\u00A0'}the most valuable things that can exist in{'\u00A0'}a{'\u00A0'}functioning market. It{'\u00A0'}protects customers. It{'\u00A0'}forces improvement. It{'\u00A0'}creates accountability that cannot be{'\u00A0'}bought.
              </BeliefItem>
              <BeliefItem>
                A{'\u00A0'}negative review that cannot be{'\u00A0'}substantiated is{'\u00A0'}not a{'\u00A0'}review. It{'\u00A0'}is{'\u00A0'}noise at{'\u00A0'}best. A{'\u00A0'}weapon at{'\u00A0'}worst.
              </BeliefItem>
              <BeliefItem>
                The difference between the two matters enormously{'\u00A0'}— and a{'\u00A0'}platform has a{'\u00A0'}responsibility to{'\u00A0'}draw that line, not to{'\u00A0'}profit from the ambiguity.
              </BeliefItem>
              <BeliefItem>
                Decisions about what gets published should be{'\u00A0'}made by{'\u00A0'}a{'\u00A0'}community applying transparent rules{'\u00A0'}— not by{'\u00A0'}algorithms optimised for engagement, not by{'\u00A0'}editorial teams answerable to{'\u00A0'}advertisers, and not by{'\u00A0'}subscription tiers.
              </BeliefItem>
              <BeliefItem>
                Every decision affecting someone's reputation should be{'\u00A0'}logged, explained, and auditable by{'\u00A0'}anyone.
              </BeliefItem>
              <BeliefItem>
                Reviewer identity is{'\u00A0'}private information protected by{'\u00A0'}law, disclosed only when a{'\u00A0'}court orders it{'\u00A0'}— not when a{'\u00A0'}business asks, not when a{'\u00A0'}platform decides, and not for any amount of{'\u00A0'}money.
              </BeliefItem>
              <BeliefItem>
                A{'\u00A0'}review platform that sells influence over its own ratings is{'\u00A0'}not a{'\u00A0'}review platform. It{'\u00A0'}is{'\u00A0'}a{'\u00A0'}protection racket with better branding.
              </BeliefItem>
            </div>
          </Chapter>

          {/* Chapter VI */}
          <Chapter number="VI" title="What we built.">
            <p className="mb-8">
              Truvio is{'\u00A0'}the structural answer to{'\u00A0'}a{'\u00A0'}structural problem.
            </p>
            <div className="space-y-4">
              <BuiltItem icon={<i className="ph-bold ph-identification-card text-2xl text-truvio-accent"></i>}>
                Every reviewer is{'\u00A0'}verified through a{'\u00A0'}licensed RegTech partner before writing a{'\u00A0'}single word. Their identity is{'\u00A0'}real, permanent, and legally accountable. People with genuine grievances have nothing to{'\u00A0'}fear. People filing reviews as{'\u00A0'}leverage understand that their name is{'\u00A0'}attached to{'\u00A0'}their claim.
              </BuiltItem>
              <BuiltItem icon={<i className="ph-bold ph-paperclip text-2xl text-truvio-accent"></i>}>
                Every review requires evidence proportional to{'\u00A0'}the severity of{'\u00A0'}the claim. An{'\u00A0'}opinion about service quality is{'\u00A0'}an{'\u00A0'}opinion. An{'\u00A0'}allegation of{'\u00A0'}fraud is{'\u00A0'}a{'\u00A0'}legal statement. We treat them as{'\u00A0'}what they are.
              </BuiltItem>
              <BuiltItem icon={<i className="ph-bold ph-scales text-2xl text-truvio-accent"></i>}>
                Negative reviews enter community moderation before publication. A{'\u00A0'}distributed group of{'\u00A0'}verified, weighted community members reviews the evidence independently{'\u00A0'}— without seeing each other's votes until they've cast their own. There is{'\u00A0'}no{'\u00A0'}central editorial team. There is{'\u00A0'}no{'\u00A0'}paid removal. The rules are open source and change only by{'\u00A0'}community vote.
              </BuiltItem>
              <BuiltItem icon={<i className="ph-bold ph-prohibit text-2xl text-truvio-accent"></i>}>
                No subscription tier, no{'\u00A0'}payment, and no{'\u00A0'}relationship with Truvio gives any party influence over a{'\u00A0'}rating or{'\u00A0'}a{'\u00A0'}moderation outcome. A{'\u00A0'}business on{'\u00A0'}our highest plan with a{'\u00A0'}1.8{'\u00A0'}rating has a{'\u00A0'}1.8{'\u00A0'}rating. This is{'\u00A0'}not a{'\u00A0'}policy we will revisit. It{'\u00A0'}is{'\u00A0'}the point of{'\u00A0'}the platform.
              </BuiltItem>
              <BuiltItem icon={<i className="ph-bold ph-lock text-2xl text-truvio-accent"></i>}>
                Reviewer identity is{'\u00A0'}stored under EU law, disclosed only by{'\u00A0'}court order from a{'\u00A0'}competent jurisdiction, and protected by{'\u00A0'}GDPR at{'\u00A0'}the architectural level{'\u00A0'}— not as{'\u00A0'}a{'\u00A0'}feature, but as{'\u00A0'}a{'\u00A0'}constraint we placed on{'\u00A0'}ourselves.
              </BuiltItem>
            </div>
          </Chapter>

          {/* Chapter VII */}
          <Chapter number="VII" title="What we are not.">
            <div className="mt-8">
              <NotItem type="negative">
                We are not anti-business. We are anti-fraud, from any direction.
              </NotItem>
              <NotItem type="negative">
                We are not trying to{'\u00A0'}make it{'\u00A0'}harder to{'\u00A0'}leave a{'\u00A0'}negative review. We are trying to{'\u00A0'}make it{'\u00A0'}impossible to{'\u00A0'}leave a{'\u00A0'}dishonest one.
              </NotItem>
              <NotItem type="negative">
                We are not neutral. We have a{'\u00A0'}position. Our position is{'\u00A0'}that honesty matters, that accountability is{'\u00A0'}not negotiable, and that the infrastructure of{'\u00A0'}trust in{'\u00A0'}a{'\u00A0'}market economy is{'\u00A0'}too important to{'\u00A0'}be{'\u00A0'}left to{'\u00A0'}platforms with conflicting financial interests.
              </NotItem>
              <NotItem type="positive">
                We are not finished. This document will change as{'\u00A0'}the platform changes. The principles won't.
              </NotItem>
            </div>
          </Chapter>

          {/* Chapter VIII */}
          <Chapter number="VIII" title="A note on what we named ourselves.">
            <p>
              Truvio. True. Review. The two words that define what we're trying to{'\u00A0'}build. Not the most reviews. Not the fastest reviews. Not the most convenient reviews.
            </p>
            <PullQuote>
              True ones.
            </PullQuote>
          </Chapter>

          {/* Footnotes */}
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-20 pt-10 border-t border-truvio-border"
          >
            <p className="text-truvio-muted/60 text-xs leading-relaxed">
              <strong>Sources:</strong> The Times, 'Estate agents accused of gaming Trustpilot review system,' March 2019. Grizzly Research LLC, 'The Trustpilot Mafia — How the Extortion Model Destroys Trustpilot's Value Proposition,' December 2025 — grizzlyreports.com/trustpilot. Truvio is registered in Cyprus, operating under EU law. Data is stored in the European Union. This manifesto is a living document and will be updated as the platform evolves.
            </p>
          </motion.div>

          {/* Final CTA */}
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-20 bg-truvio-surface border border-truvio-border p-8 md:p-12 text-center"
          >
            <h3 className="font-playfair font-black text-2xl md:text-3xl text-truvio-text mb-8">
              Ready to be part of something honest?
            </h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/" className="btn-primary">
                Join Truvio
              </Link>
              <Link to="/" className="btn-secondary">
                For businesses
              </Link>
            </div>
          </motion.div>
        </main>

        <Footer />
      </div>
    </div>
  );
}

export default ManifestoPage;
